import type { CSSProperties } from 'react'
import { useMemo } from 'react'
import '../../../../../../components/skeletons/skeleton.css'
import { resolveAllocationHeaderAppearance } from '../header/allocation/LumpsumAllocationHeader'
import { resolveIdealHeaderAppearance } from '../header/ideal/LumpsumIdealHeader'
import { resolveRebalanceHeaderAppearance } from '../header/rebalance/LumpsumRebalanceHeader'
import {
  DEFAULT_PLANS_ACTION_FILTERS,
  LUMPSUM_PLANS_ACTION_FILTERS_CLASS_NAME
} from '../header/common/ListPlansActionFilters'
import { PlansPlanMode } from '../../api/types'
import { useLumpsumPlansContext } from '../LumpsumPlansProvider'

export function HeaderHeroSkeleton() {
  const { planMode, allocationPlansData, rebalancePlansData, idealData } = useLumpsumPlansContext()
  const headerAppearance = useMemo(() => {
    if (planMode === PlansPlanMode.REBALANCE) {
      return resolveRebalanceHeaderAppearance(rebalancePlansData?.riskProfile)
    }
    if (planMode === PlansPlanMode.ALLOCATION) {
      return resolveAllocationHeaderAppearance(allocationPlansData?.riskProfile)
    }
    return resolveIdealHeaderAppearance(idealData?.riskProfile)
  }, [planMode, allocationPlansData, rebalancePlansData, idealData])
  const isIdeal = planMode === PlansPlanMode.IDEAL_PORTFOLIO
  const skeletonStyle = getHeaderHeroSkeletonStyle(headerAppearance.backgroundColor)

  return (
    <>
      <div
        className={`relative overflow-visible px-5 ${isIdeal ? 'pt-8 pb-6' : 'pt-8 pb-4'}`}
        style={skeletonStyle}
      >
        <div className="relative z-[1] flex gap-3">
          <div className="min-w-0 flex-1 max-w-[240px]">
            <div className="skeleton h-[14px] w-[128px] rounded-[4px]" />
            <div className="skeleton mt-4 h-[28px] w-[200px] rounded-[4px]" />

            {!isIdeal ? (
              <div className="flex flex-col gap-1 pt-4">
                <div className="skeleton h-[14px] w-[188px] rounded-[4px]" />
                <div className="skeleton h-[14px] w-[152px] rounded-[4px]" />
              </div>
            ) : null}

            <div className={`flex flex-col gap-1.5 ${isIdeal ? 'pt-4' : 'pt-5'}`}>
              <div className="skeleton h-[14px] w-full rounded-[4px]" />
              <div className="skeleton h-[14px] w-[88%] rounded-[4px]" />
            </div>

            {isIdeal ? <div className="skeleton mt-6 h-[14px] w-[196px] rounded-[4px]" /> : null}
          </div>
        </div>
      </div>
      {!isIdeal ? (
        <HeaderActionFiltersSkeleton backgroundColor={headerAppearance.backgroundColor} />
      ) : null}
    </>
  )
}

export function getHeaderHeroSkeletonStyle(backgroundColor: string): CSSProperties {
  return {
    '--skeleton-base': `color-mix(in srgb, ${backgroundColor} 88%, #000000)`,
    '--skeleton-highlight': `color-mix(in srgb, ${backgroundColor} 72%, #ffffff)`
  } as CSSProperties
}

export function HeaderActionFiltersSkeleton({ backgroundColor }: { backgroundColor: string }) {
  return (
    <div
      className={LUMPSUM_PLANS_ACTION_FILTERS_CLASS_NAME}
      style={getHeaderHeroSkeletonStyle(backgroundColor)}
    >
      <div className="relative z-[1] overflow-x-auto no-scrollbar overscroll-x-none px-5 pb-6 pt-1">
        <div className="flex w-max gap-[10px]">
          {DEFAULT_PLANS_ACTION_FILTERS.map((filterOption) => (
            <div
              key={filterOption}
              className="flex w-[58px] shrink-0 flex-col items-center gap-3"
            >
              <div className="skeleton h-12 w-12 rounded-full" />
              <div className="skeleton h-[12px] w-[48px] rounded-[4px]" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
